import React, { useState, useMemo, useEffect } from 'react';
import Iconify from 'src/components/Iconify';
import { Input } from 'src/@/components/ui/input';
import pluginsService from 'src/services/pluginsService';
import useAuth from 'src/hooks/useAuth';
import { PLUGINS_DESIGN as d } from './Plugins.design';

// --------------- Categorias ---------------

const CATEGORIES = [
    { id: 'all', label: 'Todos os Plugins', icon: 'eva:search-outline' },
    { id: '_separator' },
    { id: 'recursos', label: 'Recursos LAIS' },
    { id: 'automacao', label: 'Automação' },
    { id: 'crm', label: 'CRM' },
    { id: 'marketing', label: 'Marketing' },
    { id: 'mensagens', label: 'Mensagens' },
    { id: 'equipe', label: 'Equipe' },
    { id: 'outros', label: 'Outros' },
];

// --------------- Helpers ---------------

const getPlanBadge = (plan) => {
    const labels = { plus: 'Plus', essencial: 'Essencial', mini: 'Mini' };
    return { label: labels[plan] || plan, className: d.badge[plan] || d.badge.essencial };
};

const getCategoryLabel = (cat) => {
    const found = CATEGORIES.find(c => c.id === cat);
    return found?.label || cat;
};

// --------------- Components ---------------

function SidebarItem({ cat, active, onClick, count }) {
    if (cat.id === '_separator') {
        return <div className={d.sidebar.separator} />;
    }

    const isTopLevel = cat.icon;
    const isActive = active === cat.id;

    return (
        <button
            onClick={() => onClick(cat.id)}
            className={`${d.sidebar.item} ${isActive ? d.sidebar.itemActive : d.sidebar.itemInactive}`}
        >
            {isTopLevel && (
                <Iconify icon={cat.icon} width={16} className={d.sidebar.itemIcon} />
            )}
            <span className="truncate">{cat.label}</span>
            <span className={isActive ? d.sidebar.itemCountActive : d.sidebar.itemCount}>
                {count}
            </span>
        </button>
    );
}

function PluginCard({ plugin, onClick }) {
    const badge = getPlanBadge(plugin.plan);

    return (
        <div className={d.card.container} onClick={() => onClick(plugin)}>
            <div className={d.card.header}>
                <div className={`${d.card.iconBox} ${plugin.iconBg}`}>
                    <Iconify icon={plugin.icon} width={22} className={plugin.iconColor} />
                </div>
                <div className={d.card.info}>
                    <div className="flex items-center gap-2">
                        <h3 className={d.card.name}>{plugin.name}</h3>
                        <span className={badge.className}>{badge.label}</span>
                        {plugin.installed && (
                            <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-green-100 text-green-700">
                                Instalado
                            </span>
                        )}
                    </div>
                    <p className={d.card.description}>{plugin.description}</p>
                </div>
            </div>
            <div className={d.card.footer}>
                <span className={d.card.price}>{plugin.price || 'Grátis'}</span>
                <span className={d.card.installButton}>Ver detalhes →</span>
            </div>
        </div>
    );
}

function PluginDetailModal({ plugin, onClose, onToggleInstall, installing }) {
    if (!plugin) return null;

    const badge = getPlanBadge(plugin.plan);

    const permissions = plugin.permissions || [];
    const half = Math.ceil(permissions.length / 2);
    const permCol1 = permissions.slice(0, half);
    const permCol2 = permissions.slice(half);

    return (
        <>
            <div className={d.modal.backdrop} onClick={onClose} />
            <div className={d.modal.panel} onClick={onClose}>
                <div className={d.modal.container} onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div className={d.modal.header.container}>
                        <div className={`${d.modal.header.iconBox} ${plugin.iconBg}`}>
                            <Iconify icon={plugin.icon} width={36} className={plugin.iconColor} />
                        </div>
                        <div className={d.modal.header.info}>
                            <h2 className={d.modal.header.name}>{plugin.name}</h2>
                            <div className={d.modal.header.badgeRow}>
                                <span className={badge.className}>{badge.label}</span>
                                <span className="text-xs text-gray-400">•</span>
                                <span className="text-xs text-gray-500">{getCategoryLabel(plugin.category)}</span>
                                {plugin.installed && (
                                    <>
                                        <span className="text-xs text-gray-400">•</span>
                                        <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-green-100 text-green-700">
                                            Instalado
                                        </span>
                                    </>
                                )}
                            </div>
                            <p className={d.modal.header.developer}>
                                Desenvolvido por <span className="font-medium text-gray-700">{plugin.developer || 'LAIS'}</span>
                                {plugin.developerUrl && (
                                    <span className="text-[#7C3AED] ml-1">{plugin.developerUrl}</span>
                                )}
                            </p>
                        </div>
                        <button onClick={onClose} className={d.modal.header.close}>
                            <Iconify icon="eva:close-outline" width={20} />
                        </button>
                    </div>

                    {/* Body */}
                    <div className={d.modal.body}>
                        {permissions.length > 0 && (
                            <div className={d.modal.section.container}>
                                <h3 className={d.modal.section.title}>Permissões</h3>
                                <div className={d.modal.section.card}>
                                    <div className={d.modal.permissionsGrid}>
                                        {permCol1.length > 0 && (
                                            <div className={d.modal.permissionGroup}>
                                                {permCol1.map((perm, i) => (
                                                    <div key={i} className={d.modal.permissionItem}>
                                                        <Iconify icon="lucide:arrow-right" width={14} className="text-gray-400 flex-shrink-0" />
                                                        {perm}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {permCol2.length > 0 && (
                                            <div className={d.modal.permissionGroup}>
                                                {permCol2.map((perm, i) => (
                                                    <div key={i} className={d.modal.permissionItem}>
                                                        <Iconify icon="lucide:arrow-right" width={14} className="text-gray-400 flex-shrink-0" />
                                                        {perm}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className={d.modal.section.container}>
                            <h3 className={d.modal.section.title}>Sobre o plugin</h3>
                            <div className={d.modal.description}>
                                {plugin.longDescription || plugin.description}
                            </div>
                        </div>

                        {plugin.features && plugin.features.length > 0 && (
                            <div className={d.modal.section.container}>
                                <h3 className={d.modal.section.title}>Funcionalidades</h3>
                                <div className={d.modal.section.card}>
                                    <div className="space-y-2">
                                        {plugin.features.map((feat, i) => (
                                            <div key={i} className={d.modal.featureItem}>
                                                <Iconify icon="eva:checkmark-circle-2-fill" width={16} className="text-green-500 flex-shrink-0" />
                                                {feat}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className={d.modal.footer.container}>
                        <div className={d.modal.footer.left}>
                            <span className="text-sm text-gray-500">{plugin.price || 'Grátis'}</span>
                        </div>
                        {plugin.installed ? (
                            <button
                                className="flex items-center gap-2 px-5 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 text-sm font-medium rounded-xl transition-all active:transform active:scale-95 border border-red-200"
                                onClick={() => onToggleInstall(plugin)}
                                disabled={installing}
                            >
                                <Iconify icon="eva:close-circle-outline" width={18} />
                                {installing ? 'Desinstalando...' : 'Desinstalar'}
                            </button>
                        ) : (
                            <button
                                className={d.modal.footer.installBtn}
                                onClick={() => onToggleInstall(plugin)}
                                disabled={installing}
                            >
                                <Iconify icon="eva:plus-circle-outline" width={18} />
                                {installing ? 'Instalando...' : 'Instalar'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

// --------------- Main ---------------

export default function Plugins() {
    const { user, cliente } = useAuth();
    const [plugins, setPlugins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [installing, setInstalling] = useState(false);
    const [activeCategory, setActiveCategory] = useState('all');
    const [search, setSearch] = useState('');
    const [selectedPlugin, setSelectedPlugin] = useState(null);

    const clienteId = user?.clienteId;

    useEffect(() => {
        if (clienteId) loadPlugins();
    }, [clienteId]);

    const loadPlugins = async () => {
        try {
            setLoading(true);
            const response = await pluginsService.listarPorCliente(clienteId);
            if (response?.data) {
                setPlugins(response.data);
            }
        } catch (error) {
            console.error('Erro ao carregar plugins:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleInstall = async (plugin) => {
        try {
            setInstalling(true);
            if (plugin.installed) {
                await pluginsService.desinstalar(clienteId, plugin._id);
            } else {
                await pluginsService.instalar(clienteId, plugin._id);
            }

            setPlugins(prev => prev.map(p =>
                p._id === plugin._id ? { ...p, installed: !p.installed } : p
            ));

            setSelectedPlugin(prev =>
                prev?._id === plugin._id ? { ...prev, installed: !prev.installed } : prev
            );
        } catch (error) {
            console.error('Erro ao alterar plugin:', error);
        } finally {
            setInstalling(false);
        }
    };

    const filteredPlugins = useMemo(() => {
        let list = plugins;

        if (activeCategory !== 'all') {
            list = list.filter(p => p.category === activeCategory);
        }

        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter(p =>
                p.name.toLowerCase().includes(q) ||
                (p.description || '').toLowerCase().includes(q)
            );
        }

        return list;
    }, [plugins, activeCategory, search]);

    const getCategoryCount = (categoryId) => {
        if (categoryId === 'all') return plugins.length;
        return plugins.filter(p => p.category === categoryId).length;
    };

    const activeCatLabel = CATEGORIES.find(c => c.id === activeCategory)?.label || 'Todos os Plugins';

    return (
        <div className={d.page.container}>
            {/* Sidebar */}
            <div className={d.sidebar.container}>
                <div className={d.sidebar.header}>
                    <h2 className={d.sidebar.title}>Plugins</h2>
                </div>

                <nav className={d.sidebar.nav}>
                    {CATEGORIES.map((cat, i) => (
                        <React.Fragment key={cat.id || i}>
                            {cat.id !== '_separator' && !cat.icon && i > 0 && CATEGORIES[i - 1]?.id === '_separator' && (
                                <div className={d.sidebar.sectionLabel}>Categorias</div>
                            )}
                            <SidebarItem
                                cat={cat}
                                active={activeCategory}
                                onClick={setActiveCategory}
                                count={getCategoryCount(cat.id)}
                            />
                        </React.Fragment>
                    ))}
                </nav>
            </div>

            {/* Content */}
            <div className={d.content.container}>
                {/* Header */}
                <div className={d.content.header.container}>
                    <div className={d.content.header.left}>
                        <div className={d.content.header.iconBox}>
                            <Iconify icon="lucide:puzzle" width={20} className="text-violet-600" />
                        </div>
                        <div>
                            <h1 className={d.content.header.title}>{activeCatLabel}</h1>
                            <p className={d.content.header.subtitle}>{filteredPlugins.length} plugins disponíveis</p>
                        </div>
                    </div>

                    <div className={d.content.searchWrapper}>
                        <Iconify icon="eva:search-outline" width={16} className={d.content.searchIcon} />
                        <Input
                            placeholder="Buscar plugins..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className={d.content.searchInput}
                        />
                    </div>
                </div>

                {/* Grid */}
                {loading ? (
                    <div className={d.content.loadingContainer}>
                        <Iconify icon="mdi:loading" width={32} className="animate-spin text-[#7C3AED] mb-3" />
                        <span className="text-sm text-gray-500">Carregando plugins...</span>
                    </div>
                ) : filteredPlugins.length > 0 ? (
                    <div className={d.content.grid}>
                        {filteredPlugins.map((plugin) => (
                            <PluginCard
                                key={plugin._id || plugin.slug}
                                plugin={plugin}
                                onClick={setSelectedPlugin}
                            />
                        ))}
                    </div>
                ) : (
                    <div className={d.emptyState.container}>
                        <div className={d.emptyState.iconBox}>
                            <Iconify icon="lucide:puzzle" width={32} className="text-gray-400" />
                        </div>
                        <h3 className={d.emptyState.title}>Nenhum plugin encontrado</h3>
                        <p className={d.emptyState.subtitle}>
                            Tente buscar com outros termos ou selecione outra categoria.
                        </p>
                    </div>
                )}
            </div>

            {/* Modal de detalhes */}
            <PluginDetailModal
                plugin={selectedPlugin}
                onClose={() => setSelectedPlugin(null)}
                onToggleInstall={handleToggleInstall}
                installing={installing}
            />
        </div>
    );
}
